import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { formatDateTime } from '@/lib/format'
import type { BlockSource, BlockType } from '../types'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
  pending: boolean
  preview: {
    type: BlockType
    targetLabel: string
    reason: string
    source: BlockSource
    sourceReference: string | null
    effectiveFrom: string
    effectiveTo: string | null
    isRegulatory: boolean
  }
}

/**
 * AF-128 — final preview before submission. Shows what will be blocked,
 * the reason, source, and duration so analysts can spot mistakes (wrong
 * NUBAN, wrong duration) before the request hits the approval queue.
 */
export function ConfirmRequestDialog({
  open,
  onOpenChange,
  onConfirm,
  pending,
  preview,
}: Props) {
  const duration = preview.effectiveTo
    ? `${formatDateTime(preview.effectiveFrom)} → ${formatDateTime(preview.effectiveTo)}`
    : `${formatDateTime(preview.effectiveFrom)} · indefinite`
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirm block request</DialogTitle>
          <DialogDescription>
            This goes into the maker-checker queue. An approver other than you
            must approve before the block becomes active.
          </DialogDescription>
        </DialogHeader>

        <dl className="flex flex-col gap-2 text-sm">
          <Row label="Type">
            <Badge variant="default" className="capitalize">
              {preview.type}
            </Badge>
          </Row>
          <Row label="Target">
            <span className="font-medium">{preview.targetLabel}</span>
          </Row>
          <Row label="Reason">
            <p className="max-w-prose whitespace-pre-line text-sm">{preview.reason}</p>
          </Row>
          <Row label="Source">
            <span className="capitalize">{preview.source.replace('_', ' ')}</span>
            {preview.sourceReference && (
              <span className="ml-2 font-mono text-xs text-muted-fg">
                {preview.sourceReference}
              </span>
            )}
          </Row>
          <Row label="Duration">
            <span className="font-mono text-xs">{duration}</span>
          </Row>
          {preview.isRegulatory && (
            <p className="rounded border border-priority-critical/30 bg-priority-critical/5 px-2 py-1.5 text-xs text-priority-critical">
              Marked as <strong>regulatory</strong>. Lifting will require legal-team approval.
            </p>
          )}
        </dl>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Back
          </Button>
          <Button onClick={onConfirm} disabled={pending}>
            {pending ? 'Submitting…' : 'Submit request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[7rem_1fr] gap-2">
      <dt className="text-xs uppercase tracking-wide text-muted-fg">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  )
}
